import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import { getNedenBizPage } from '../services/api';
import DynamicIcon from '../components/DynamicIcon';
import defaultNedenBiz from '../default_datas/pages/defaultNedenBiz';

const NedenBiz = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tx = (tr, en) => lang === 'en' ? (en || tr) : tr;

  useEffect(() => {
    getNedenBizPage()
      .then(result => { setData(result || defaultNedenBiz); setLoading(false); })
      .catch(err => { console.error('Strapi fetch hatası:', err); setData(defaultNedenBiz); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#2a9d8f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = data?.pageContent || [];
  const hero = sections.find(s => s.__component === 'sections.hero');
  const statsSection = sections.find(s => s.__component === 'sections.stats-section');
  const quoteSection = sections.find(s => s.__component === 'sections.quote-section');
  const featureSections = sections.filter(s => s.__component === 'sections.features-section');
  const testimonialsSection = sections.find(s => s.__component === 'sections.testimonials-section');

  const mainFeatures = featureSections[0];
  const specialFeatures = featureSections[1];

  return (
    <>
      <Seo
        title={lang === 'en' ? 'Why RUZY? | ISO 27001 Compliance Platform' : 'Neden RUZY? | ISO 27001 Uyumluluk Platformu'}
        description={lang === 'en'
          ? 'Discover the experiences of 500+ customers who chose RUZY.'
          : 'RUZY\'yi tercih eden 500+ müşterinin deneyimlerini keşfedin.'}
        path="/neden-biz"
      />
      <div className="min-h-screen bg-[#f5faf9] font-poppins text-[#0D3A2F] overflow-x-hidden">

        {/* ── 1. HERO ── */}
        {hero && (
          <ScrollReveal>
            <section className="bg-[#0D3A2F] py-16 lg:py-24 px-6 lg:px-28 text-center">
              {hero.badge && (
                <span className="inline-block bg-[rgba(42,157,143,0.3)] text-[#2a9d8f] text-[10px] lg:text-xs font-bold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase">
                  {tx(hero.badge, hero.badge_en)}
                </span>
              )}
              <h1 className="text-3xl lg:text-5xl font-black text-white leading-tight mb-6 max-w-3xl mx-auto">
                {tx(hero.title, hero.title_en)}
              </h1>
              <p className="text-sm lg:text-lg text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
                {tx(hero.description, hero.description_en)}
              </p>
              {hero.primaryButtonText && (
                <button
                  onClick={() => navigate('/iletisim')}
                  className="w-full sm:w-auto bg-[#2a9d8f] text-white px-8 py-4 rounded-xl text-base font-bold border-none cursor-pointer hover:bg-[#238a7e] transition-colors"
                >
                  {tx(hero.primaryButtonText, hero.primaryButtonText_en)}
                </button>
              )}
            </section>
          </ScrollReveal>
        )}

        {/* ── 2. İSTATİSTİKLER (Mobil Kompakt) ── */}
        {statsSection?.stats?.length > 0 && (
          <ScrollReveal>
            <section className="bg-white py-10 lg:py-14 px-6 lg:px-28">
              <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
                {statsSection.stats.map((s, i) => (
                  <div key={i} className="text-center border border-[#e0eeeb] rounded-xl lg:rounded-2xl py-6 lg:py-8 px-4 lg:px-6">
                    {/* İKON EKLE */}
                    {s.icon && (
                      <div className="flex justify-center mb-3 text-[#2a9d8f]">
                        <DynamicIcon name={s.icon} size={28} />
                      </div>
                    )}
                    <div className="text-2xl lg:text-5xl font-black text-[#2a9d8f] mb-1 lg:mb-2">
                      {tx(s.value, s.value_en) || s.value}
                    </div>
                    <div className="text-[10px] lg:text-sm text-[#4a6b63] font-bold uppercase tracking-wide lg:font-medium lg:normal-case">
                      {tx(s.title, s.title_en)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 3. TEK MÜŞTERİ ALINTISI ── */}
        {quoteSection && (
          <ScrollReveal>
            <section className="bg-[#f5faf9] py-12 lg:py-16 px-6 lg:px-28 text-center">
              <div className="max-w-2xl mx-auto">
                <div className="text-4xl lg:text-5xl text-[#2a9d8f] mb-2 lg:mb-4">"</div>
                <p className="text-base lg:text-xl text-[#0D3A2F] font-medium leading-relaxed mb-4 lg:mb-6 italic">
                  {tx(quoteSection.quote, quoteSection.quote_en)}
                </p>
                <span className="text-xs lg:text-sm text-[#4a6b63] font-semibold">
                  {tx(quoteSection.author, quoteSection.author_en)}
                </span>
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 4. NEDEN 500+ MÜŞTERİ (Gridcols-2 Mobil) ── */}
        {mainFeatures && (
          <ScrollReveal>
            <section className="bg-white py-12 lg:py-20 px-6 lg:px-28">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-10 lg:mb-14">
                  <h2 className="text-2xl lg:text-4xl font-black text-[#0D3A2F] mb-4 leading-tight">
                    {tx(mainFeatures.Title, mainFeatures.Title_en)}
                  </h2>
                  {mainFeatures.Description && (
                    <p className="text-sm lg:text-base text-[#4a6b63] max-w-xl mx-auto leading-relaxed">
                      {tx(mainFeatures.Description, mainFeatures.Description_en)}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                  {(mainFeatures.cards || []).map((f, i) => (
                    <div key={i} className="bg-[#f5faf9] border border-[#e0eeeb] rounded-2xl p-6 lg:p-8 flex flex-col items-center lg:items-start text-center lg:text-left">
                      <div className="text-[#2a9d8f] mb-4 lg:mb-5">
                        <DynamicIcon name={f.icon} size={32} />
                      </div>
                      <h3 className="text-base lg:text-lg font-bold text-[#0D3A2F] mb-2 lg:mb-3">
                        {tx(f.title, f.title_en)}
                      </h3>
                      <p className="text-xs lg:text-sm text-[#4a6b63] leading-relaxed">
                        {tx(f.description, f.description_en)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 5. BİZİ ÖZEL YAPAN ── */}
        {specialFeatures && (
          <ScrollReveal>
            <section className="bg-[#f5faf9] py-12 lg:py-20 px-6 lg:px-28">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl lg:text-4xl font-black text-[#0D3A2F] mb-10 lg:mb-14 text-center leading-tight">
                  {tx(specialFeatures.Title, specialFeatures.Title_en)}
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-10">
                  {(specialFeatures.cards || []).map((f, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 lg:p-8 border border-[#e0eeeb] text-center flex flex-col items-center">
                      <div className="text-[#2a9d8f] mb-3 lg:mb-5">
                        <DynamicIcon name={f.icon} size={28} />
                      </div>
                      <h3 className="text-xs lg:text-xl font-bold text-[#0D3A2F] mb-2 lg:mb-4">
                        {tx(f.title, f.title_en)}
                      </h3>
                      <p className="hidden sm:block text-xs lg:text-sm text-[#4a6b63] leading-relaxed">
                        {tx(f.description, f.description_en)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 6. MÜŞTERİ YORUMLARI ── */}
        {testimonialsSection && (
          <ScrollReveal>
            <section className="bg-[#0D3A2F] py-12 lg:py-20 px-6 lg:px-28">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl lg:text-4xl font-black text-white text-center mb-10 lg:mb-12 leading-tight">
                  {tx(testimonialsSection.sectionTitle, testimonialsSection.sectionTitle_en)}
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
                  {(testimonialsSection.items || []).map((k, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 lg:p-8">
                      <p className="text-white/80 text-sm lg:text-base leading-relaxed mb-6 italic">
                        "{tx(k.quote, k.quote_en)}"
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-[#2a9d8f] flex items-center justify-center text-white font-bold text-xs lg:text-sm flex-shrink-0">
                          {tx(k.name, k.name_en)?.charAt(0)}
                        </div>
                        <div>
                          <div className="text-white font-semibold text-xs lg:text-sm">
                            {tx(k.name, k.name_en)}
                          </div>
                          <div className="text-white/50 text-[10px] lg:text-xs">
                            {tx(k.title, k.title_en)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

      </div>
    </>
  );
};

export default NedenBiz;